import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Badge, Dropdown } from "react-bootstrap";
import { useLocation, useParams } from "react-router-dom";
import { Search, MoreVertical } from "lucide-react";
import { debounce } from "lodash";
import toast from "react-hot-toast";
import { TableLoaderRow, TableEmptyRow } from "../components/TableLoader";
import Pagination from "../components/Pagination";
import {
  getUserEventGuest,
  updateGuestStatus,
} from "../../services/UserServices/UserService";
import PageTitle from "../layouts/PageTitle";

export default function Guest() {
  const location = useLocation();
  const historyData = location.state;
  const [loader, setLoader] = useState(false);
  const [itemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { id } = useParams();
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [search, setSearch] = useState("");
  const [user, setUser] = useState([]);
  const [loading, setLoading] = useState(true);

  const getData = async () => {
    try {
      setLoading(true);
      const res = await getUserEventGuest(id, currentPage, 10, debouncedSearch);
      setUser(res?.data?.data?.guests || []);
      setTotalPages(res?.data?.data?.count || 0);
    } catch (error) {
      console.error(error?.response?.data?.message);
      setUser([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getData();
  }, [currentPage, debouncedSearch, id]);

  const debouncedHandler = useMemo(
    () =>
      debounce((value) => {
        if (value.length >= 1 || value.length === 0) {
          setDebouncedSearch(value);
          setCurrentPage(0);
        }
      }, 800),
    []
  );

  useEffect(() => {
    return () => {
      debouncedHandler.cancel();
    };
  }, [debouncedHandler]);

  const updateStatus = async (data, status) => {
    try {
      setLoader(true);
      const payload = {
        hostId: data?.invite?.host,
        guestIds: [data?._id],
        status,
        eventId: data?.invite?.event,
      };
      await updateGuestStatus(payload);
      toast.success("Status updated");
      getData();
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  const totalItems = totalPages;
  const total = Math.ceil(totalItems / 10);

  const rsvpBadge = (status = "") => {
    const value = status.toLowerCase();
    const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : "—";
    if (value === "accepted") return <Badge bg="success">{label}</Badge>;
    if (value === "rejected") return <Badge bg="danger">{label}</Badge>;
    if (value === "pending") return <Badge bg="warning" text="dark">{label}</Badge>;
    return <Badge bg="secondary">{label}</Badge>;
  };

  return (
    <div className="qx-page">
      <PageTitle
        breadcrumbs={[
          { label: "Event List", path: "/events" },
          {
            label: historyData?.name ? historyData?.name : "Event",
            path: `/event_details/${id}`,
            state: { tension: historyData },
          },
          { label: "Guests" },
        ]}
      />

      <div className="qx-toolbar mb-3">
        <div className="qx-search-wrap qx-search-wrap--icon">
          <Search size={16} className="qx-search-icon" />
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or phone number"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              debouncedHandler(e.target.value);
            }}
          />
          {search ? (
            <button
              type="button"
              className="qx-search-clear"
              aria-label="Clear search"
              onClick={() => {
                setSearch("");
                debouncedHandler("");
              }}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      <Card className="qx-table-card">
        <Card.Body>
          <Table responsive className="mb-0">
            <thead>
              <tr>
                <th>
                  <strong>S.NO</strong>
                </th>
                <th>
                  <strong>Name</strong>
                </th>
                <th>
                  <strong>Phone Number</strong>
                </th>
                <th>
                  <strong>RSVP Status</strong>
                </th>
                <th>
                  <strong>Action</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading || loader ? (
                <TableLoaderRow colSpan={5} message="Loading guests..." />
              ) : !user?.length ? (
                <TableEmptyRow colSpan={5} message="No guests found" />
              ) : (
                user.map((guest, index) => (
                  <tr key={guest?._id || index}>
                    <td>{index + 1 + currentPage * itemsPerPage}</td>
                    <td>
                      <span className="w-space-no">{guest?.name || "—"}</span>
                    </td>
                    <td>
                      <span className="w-space-no">
                        {guest?.countryCode} {guest?.phoneNumber}
                      </span>
                    </td>
                    <td>{rsvpBadge(guest?.invite?.status)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="info light"
                          className="qx-action-toggle"
                          aria-label="Guest actions"
                        >
                          <MoreVertical size={16} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {guest?.invite?.status === "pending" && (
                            <Dropdown.Item onClick={() => updateStatus(guest, "accepted")}>
                              Accept
                            </Dropdown.Item>
                          )}
                          {guest?.invite?.status === "accepted" && (
                            <Dropdown.Item onClick={() => updateStatus(guest, "rejected")}>
                              Reject
                            </Dropdown.Item>
                          )}
                          {guest?.invite?.status === "rejected" && (
                            <Dropdown.Item onClick={() => updateStatus(guest, "accepted")}>
                              Accept
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {!loading && !loader && user?.length > 0 && (
            <Pagination
              pageCount={total}
              pageValue={currentPage}
              setPage={setCurrentPage}
              total={totalItems}
              pageSize={itemsPerPage}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
